import type { ReactElement } from 'react'
import RequireGrant from '../../auth/RequireGrant'
import type { GrantCode } from '../../auth/grants'
import { MODULE_ITEMS } from '../nav/menu'

/**
 * Menüdeki görünürlük yetkisini (`menu.ts` → `viewGrants`) **rota kilidi** olarak da uygular
 * (sızma testi BGVL-137394, 24.08.2026): menüde görünmeyen bir ekran, adresi elle yazılarak da
 * açılamaz. Bulguda yetkisiz bir kullanıcı `/admin/users`'ı doğrudan URL ile açabiliyordu; uçlar 403
 * döndüğü için veri sızmıyordu ama boş bir yönetim ekranı görünüyordu.
 *
 * <p>Tek kaynak menüdür: yetki rotada **tekrar yazılmaz**, `menu.ts`'ten okunur — yeni eklenen her
 * ekran kendiliğinden korunur ve iki listenin ayrışması mümkün olmaz.
 *
 * <p><b>Menü yetkisi, ucun yetkisiyle aynı kümeyi göstermelidir.</b> Uç `HAS_ANY(...)` gibi bileşik bir
 * kapı kullanıyorsa `viewGrants` de o kodların hepsini taşımalıdır; aksi hâlde sunucunun izin verdiği
 * bir kullanıcı ekrandan kilitlenir (ör. `/minutes/meeting-minutes/meetings` → `MEETING_GROUP_VIEW` **ve** `MANAGE`).
 *
 * <p>Bu yalnız UX/erişim kilididir; **asıl koruma backend'dedir** (FR-009).
 */
export const MENU_VIEW_GRANTS: Record<string, [GrantCode, ...GrantCode[]]> = Object.fromEntries(
  MODULE_ITEMS.filter(
    (item): item is typeof item & { path: string; viewGrants: [GrantCode, ...GrantCode[]] } =>
      !!item.path && !!item.viewGrants,
  ).map((item) => [item.path, item.viewGrants]),
)

/**
 * Rotanın menü yetkisi varsa `RequireGrant` ile sarar; yoksa öğeyi olduğu gibi döndürür (henüz yetkiye
 * bağlanmamış ekranlar eskisi gibi serbest kalır).
 */
export function withMenuGrant(path: string, element: ReactElement): ReactElement {
  const grants = MENU_VIEW_GRANTS[path]
  return grants ? <RequireGrant anyOf={grants}>{element}</RequireGrant> : element
}
